/**
 * DomainSparkline — compact 8-bucket inline-SVG trend line for the analytics
 * table. Null buckets render as gaps (no interpolation). Stroke colour is
 * derived from the last-vs-first delta so a green line = improving, red =
 * regressing, grey = flat.
 */
export const DomainSparkline = ({ series = [] }) => {
  const W = 80;
  const H = 22;
  const PAD = 2;
  const values = series.map(p => p && typeof p.avg_score === 'number' ? p.avg_score : null);
  const withData = values.filter(v => v !== null);
  if (withData.length < 2) {
    return <span className="text-[10px] text-[#9CA3AF]" title="Not enough weekly data">—</span>;
  }
  // Fixed 0-100 domain keeps sparklines comparable across domains.
  const min = 0;
  const max = 100;
  const xStep = (W - PAD * 2) / Math.max(series.length - 1, 1);
  const yScale = (v) => H - PAD - ((v - min) / (max - min)) * (H - PAD * 2);
  // Build one polyline segment per contiguous run of non-null points (so null weeks render as gaps).
  const segments = [];
  let cur = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (cur.length >= 2) segments.push(cur);
      cur = [];
    } else {
      cur.push([PAD + i * xStep, yScale(v)]);
    }
  });
  if (cur.length >= 2) segments.push(cur);

  const first = withData[0];
  const last = withData[withData.length - 1];
  const color = last - first >= 2 ? '#34C759' : last - first <= -2 ? '#FF3B30' : '#9CA3AF';
  const dots = values.map((v, i) => v === null ? null : (
    <circle key={i} cx={PAD + i * xStep} cy={yScale(v)} r={1.5} fill={color}>
      <title>{`Week ${i - series.length + 1 === 0 ? 'now' : (i - series.length + 1)} · ${v}% (n=${series[i].count})`}</title>
    </circle>
  ));
  return (
    <svg width={W} height={H} className="inline-block align-middle" aria-label="8-week sparkline">
      {segments.map((seg, idx) => (
        <polyline
          key={idx}
          points={seg.map(p => p.join(',')).join(' ')}
          fill="none"
          stroke={color}
          strokeWidth="1.3"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {dots}
    </svg>
  );
};
